/**
 * Maps API URL path segments to human-readable names for use in log
 * `event.action` and other observability fields.
 *
 * Each entry is a [urlSubstring, friendlyName] pair. The first match wins,
 * so more-specific paths should be listed before shorter ones.
 *
 * Usage:
 *   getFriendlyApiName('https://api.target.com/mpm_aggregations/v1/payroll/location/daily?key=...')
 *   // => 'Payroll Location Daily'
 */
const API_NAME_MAP: [string, string][] = [
    // BI / Greenfield
    ['/bi_reporting_assets/v3/execute_cards', 'Greenfield Cards'],
    ['/mpm_aggregations/v1/greenfield/cards', 'Greenfield Cards'],

    // Fulfillment
    ['/fulfillment_api/v1', 'Fulfillment API'],
    [
        '/mpm_aggregations/v1/fulfillment_capabilities/non_stores',
        'Fulfillment Capabilities Non-Stores',
    ],
    [
        '/mpm_aggregations/v1/fulfillment_capabilities',
        'Fulfillment Capabilities',
    ],
    [
        '/mpm_fulfillment_analytics/v1/ndd_metrics/districts',
        'NDD Metrics Districts',
    ],
    ['/mpm_fulfillment_analytics/v1/ndd_metrics/groups', 'NDD Metrics Groups'],
    [
        '/mpm_fulfillment_analytics/v1/ndd_metrics/locations',
        'NDD Metrics Locations',
    ],
    [
        '/mpm_fulfillment_analytics/v1/ndd_metrics/regions',
        'NDD Metrics Regions',
    ],
    ['/mpm_fulfillment_analytics/v1/ndd_metrics', 'NDD Metrics'],
    ['/mpm_fulfillment_analytics/v1/districts', 'Fulfillment Districts'],
    ['/mpm_fulfillment_analytics/v1/groups', 'Fulfillment Groups'],
    ['/mpm_fulfillment_analytics/v1/regions', 'Fulfillment Regions'],
    ['/mpm_fulfillment_analytics/v1/stores', 'Fulfillment Stores'],

    // Location
    ['/location_proximities/v1/nearby_locations', 'Nearby Locations'],
    ['/mpm_aggregations/v1/location_hierarchy', 'Location Hierarchy'],

    // Assets Protection
    [
        '/mpm_aggregations/v1/assets_protection/payroll',
        'Assets Protection Payroll',
    ],
    [
        '/mpm_aggregations/v1/assets_protection/secure',
        'Assets Protection Secure',
    ],
    ['/mpm_aggregations/v1/assets_protection/theft', 'Assets Protection Theft'],

    // Payroll
    ['/mpm_aggregations/v1/payroll/overtime', 'Payroll Overtime'],
    ['/mpm_aggregations/v1/payroll/workgroup', 'Payroll Workgroup'],

    // Sales
    [
        '/mpm_aggregations/v1/sales/merchandise/groups',
        'Sales Merchandise Groups',
    ],
    ['/mpm_aggregations/v1/sales/merchandise', 'Sales Merchandise'],
    ['/mpm_aggregations/v1/sales/work_areas/groups', 'Sales Work Areas Groups'],
    ['/mpm_aggregations/v1/sales/work_areas', 'Sales Work Areas'],
    ['/store_sales/v2/stores', 'Store Sales'],

    // Shortage
    [
        '/mpm_aggregations/v1/shortage/merchandise/groups',
        'Shortage Merchandise Groups',
    ],
    ['/mpm_aggregations/v1/shortage/merchandise', 'Shortage Merchandise'],
    ['/mpm_aggregations/v1/shortage', 'Shortage'],

    // Safety
    ['/mpm_aggregations/v1/safety/lagging', 'Safety Lagging'],
    ['/mpm_aggregations/v1/safety/leading', 'Safety Leading'],
    ['/mpm_aggregations/v1/safety/store', 'Safety Store'],
    ['/mpm_aggregations/v1/safety/total', 'Safety Total'],

    // Red Cards
    ['/mpm_aggregations/v1/redcards/counts', 'Red Card Counts'],
    [
        '/redcard_allocation_statistics/v1/non_stores',
        'Red Card Allocation Non-Stores',
    ],
    ['/redcard_allocation_statistics/v1/stores', 'Red Card Allocation Stores'],

    // Trailers
    [
        '/mpm_aggregations/v1/inbound_trailers/store_trailers/eta_events',
        'Inbound Trailer ETA Events',
    ],
    [
        '/mpm_aggregations/v1/inbound_trailers/store_trailers',
        'Inbound Store Trailers',
    ],
    [
        '/mpm_aggregations/v1/inbound_trailers/rgd_trailers',
        'Inbound RGD Trailers',
    ],
    [
        '/mpm_aggregations/v1/inbound_trailers/trailer_schedules',
        'Trailer Schedules',
    ],

    // Brands
    ['/mpm_aggregations/v1/brands/trends', 'Brand Trends'],
    ['/mpm_brand_sales/v2/brand_sales', 'Brand Sales'],
    ['/mpm_brand_sales/v2/brands', 'Brands'],

    // Item Details
    ['/mpm_aggregations/v1/item_details', 'Item Details'],

    // User / Preferences
    ['/mpm_aggregations/v1/user_contexts', 'User Contexts'],
    ['/mpm_team_members/v2/users', 'Team Members'],

    // UI Data
    ['/mpm_ui_data/v1/dimensions', 'UI Dimensions'],
    ['/mpm_ui_data/v1/performance_metrics', 'Performance Metrics'],
    ['/mpm_ui_data/v1/store-recalls', 'Store Recalls'],

    // Notes / Feedback
    ['/mpm_digital_notes/v2/notes/items', 'Digital Notes'],
    ['/mpm_feedbacks/v1', 'Feedback'],

    // Store Info
    ['/store_leaders/v1/storefacts', 'Store Facts'],

    // Auth / Firebase
    [
        'v1/projects/tgt-legato-firebase/namespaces/firebase:fetch',
        'Firebase Remote Config',
    ],
    ['/.well-known/openid-configuration', 'OpenID Configuration'],
    ['/openid/connect/jwks.json', 'OpenID JWKS'],
]

/**
 * Returns a human-readable API name for a given URL by matching against
 * known path segments. Returns 'Unknown API' if no match is found.
 *
 * @example
 * getFriendlyApiName('/mpm_aggregations/v1/payroll/location/daily?key=abc')
 * // => 'Payroll Location Daily'
 */
export function getFriendlyApiName(url: string): string {
    for (const [segment, name] of API_NAME_MAP) {
        if (url.includes(segment)) {
            return name
        }
    }
    return 'Unknown API'
}
